// Рендер живого веб-табло AntScore (WEB1 десктоп 16:9 / WEB4 планшет — один адаптив)
// и экрана результата WebResult (WEB5). Только просмотр: никаких элементов ввода.
//
// Состав WEB1/WEB4 (канон design v6):
//   - top-bar: лого + название корта + «Падел · парный матч» + LIVE (пульс) + таймер;
//   - грид по командам: цветной маркер, имя, мяч подачи у подающей команды,
//     ячейки по сетам (С1 С2 С3) и ОГРОМНОЕ очко справа;
//   - bottom-bar: фаза webPhase + версия + блок «Следить» (ссылка + QR).
// При finished табло уступает место WebResult, а таймер замораживается (см. main.ts).

import { computeState, type Match, type Snapshot, type Team } from './engine';
import { webPhase, webColumns, resultModel, shareUrl, teamName, type WebColumn } from './view';
import { qrSvg } from './qr';

const SERVE_BALL_SVG = `<svg viewBox="0 0 24 24" class="ball-svg" aria-hidden="true"><circle cx="12" cy="12" r="10.5" fill="#C6E84F"/><path d="M4.5 5.5C9 9 9 15 4.5 18.5" fill="none" stroke="#f1fbb2" stroke-width="1.6" stroke-linecap="round"/><path d="M19.5 5.5C15 9 15 15 19.5 18.5" fill="none" stroke="#f1fbb2" stroke-width="1.6" stroke-linecap="round"/></svg>`;

const LOGO_SVG = `<span class="logo"><span class="logo__ring"></span></span>`;

const CLOCK_SVG = `<svg viewBox="0 0 24 24" class="ico" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7.5V12l3 1.8"/></svg>`;

const TROPHY_SVG = `<svg viewBox="0 0 24 24" class="trophy-svg" fill="none" stroke="#FF7A2D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 4h12v4a6 6 0 0 1-12 0V4z"/><path d="M6 6H3v1a4 4 0 0 0 4 4M18 6h3v1a4 4 0 0 1-4 4"/><path d="M9 17h6M10 21h4M12 17v4"/></svg>`;

export interface BoardOptions {
  courtName?: string;
  appVersion?: string;
  shareId?: string;
  origin?: string;
}

export class Scoreboard {
  private root: HTMLElement;
  private timerEl: HTMLElement | null = null;
  private startTs: number | null = null;
  private frozenSec: number | null = null;
  private timerHandle: ReturnType<typeof setInterval> | null = null;
  private opts: Required<BoardOptions>;

  constructor(root: HTMLElement, opts: BoardOptions = {}) {
    this.root = root;
    this.opts = {
      courtName: opts.courtName ?? 'Клубный корт',
      appVersion: opts.appVersion ?? 'v1.0.0',
      shareId: opts.shareId ?? '',
      origin: opts.origin ?? (typeof window !== 'undefined' ? window.location.origin : ''),
    };
  }

  startTimer(): void {
    if (this.timerHandle) return;
    this.timerHandle = setInterval(() => this.renderTimer(), 1000);
  }

  stopTimer(): void {
    if (this.timerHandle) clearInterval(this.timerHandle);
    this.timerHandle = null;
  }

  /** Текущее значение таймера в формате mm:ss / h:mm:ss. */
  private clockText(): string {
    let sec: number;
    if (this.frozenSec !== null) sec = this.frozenSec;
    else if (this.startTs === null) sec = 0;
    else sec = Math.floor((Date.now() - this.startTs) / 1000);
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const ss = sec % 60;
    const pad = (n: number) => String(n).padStart(2, '0');
    return h > 0 ? `${pad(h)}:${pad(m)}:${pad(ss)}` : `${pad(m)}:${pad(ss)}`;
  }

  render(match: Match, mode: 'live' | 'demo'): void {
    const s = computeState(match);

    // Таймер: старт с первым очком; заморозка финального значения при finished.
    if (s.pointLogLength > 0 && this.startTs === null) this.startTs = Date.now();
    if (s.pointLogLength === 0) {
      this.startTs = null;
      this.frozenSec = null;
    }
    if (s.status === 'finished') {
      if (this.frozenSec === null) {
        this.frozenSec = this.startTs === null ? 0 : Math.floor((Date.now() - this.startTs) / 1000);
      }
    } else {
      this.frozenSec = null;
    }

    this.root.innerHTML =
      s.status === 'finished' ? this.resultMarkup(s, mode) : this.boardMarkup(s, mode);
    this.timerEl = this.root.querySelector('[data-timer]');
    this.renderTimer();
  }

  private renderTimer(): void {
    if (!this.timerEl) return;
    this.timerEl.textContent = this.clockText();
  }

  // ---------- WEB1 / WEB4 живое табло ----------

  private boardMarkup(s: Snapshot, mode: 'live' | 'demo'): string {
    const cols = webColumns(s);
    const live = mode === 'live';
    return `
      <div class="wb-board" data-screen="board">
        <header class="wb-top">
          <div class="wb-brand">
            ${LOGO_SVG}
            <div class="wb-brand__txt">
              <div class="wb-court">${escapeHtml(this.opts.courtName)}</div>
              <div class="wb-sub">Падел · парный матч</div>
            </div>
          </div>
          <div class="wb-status">
            <span class="wb-live ${live ? '' : 'wb-live--demo'}">
              <span class="wb-live__dot"></span>${live ? 'LIVE' : 'ДЕМО'}
            </span>
            <span class="wb-clock">${CLOCK_SVG}<span data-timer>00:00</span></span>
          </div>
        </header>

        <div class="wb-grid">
          <div class="wb-grid__head">
            <div></div>
            ${cols.map((c) => this.headCell(c)).join('')}
            <div class="wb-h wb-h--pt">ОЧКО</div>
          </div>
          ${this.teamRow(s, 'A', cols)}
          <div class="wb-divider"></div>
          ${this.teamRow(s, 'B', cols)}
        </div>

        <footer class="wb-bottom">
          <div class="wb-bottom__l">
            <span class="wb-phase"><span class="wb-phase__dot"></span>${escapeHtml(webPhase(s))}</span>
            <span class="wb-ver">AntScore · ${escapeHtml(this.opts.appVersion)}</span>
          </div>
          ${this.followBlock()}
        </footer>
      </div>
    `;
  }

  private headCell(c: WebColumn): string {
    return `<div class="wb-h wb-h--set ${c.current ? 'is-current' : ''}">${c.label}</div>`;
  }

  private teamRow(s: Snapshot, t: Team, cols: WebColumn[]): string {
    const isA = t === 'A';
    const name = teamName(s, t);
    const players = s.teams[t]?.players ?? [];
    const serving = s.server === t && s.status !== 'finished';
    const point = isA ? s.displayA : s.displayB;
    const cls = isA ? 'a' : 'b';

    const setCells = cols
      .map((c) => {
        const v = isA ? c.a : c.b;
        const state = c.empty ? 'is-empty' : c.current ? 'is-current' : 'is-done';
        return `<div class="wb-cell ${state}">${escapeHtml(v)}</div>`;
      })
      .join('');

    return `
      <div class="wb-team wb-team--${cls}" data-team="${t}">
        <div class="wb-team__id">
          <span class="wb-marker"></span>
          <div>
            <div class="wb-name-row">
              <span class="wb-name" data-team-name="${t}">${escapeHtml(name)}</span>
              ${serving ? `<span class="wb-ball" data-serving="${t}" title="подача">${SERVE_BALL_SVG}</span>` : ''}
            </div>
            ${players.length ? `<div class="wb-players">${escapeHtml(players.join(' · '))}</div>` : ''}
          </div>
        </div>
        ${setCells}
        <div class="wb-point" data-points="${t}">${escapeHtml(point)}</div>
      </div>
    `;
  }

  private followBlock(): string {
    const sid = this.opts.shareId;
    if (!sid) return '<div class="wb-follow wb-follow--empty"></div>';
    const url = shareUrl(sid, this.opts.origin);
    return `
      <div class="wb-follow">
        <div class="wb-follow__txt">
          <div class="wb-follow__cap">Следить за матчем</div>
          <div class="wb-follow__url">${escapeHtml(url)}</div>
        </div>
        <div class="wb-qr">${qrSvg(sid, 60)}</div>
      </div>
    `;
  }

  // ---------- WEB5 WebResult ----------

  private resultMarkup(s: Snapshot, mode: 'live' | 'demo'): string {
    const r = resultModel(s, this.clockText());
    const sid = this.opts.shareId;
    const url = sid ? shareUrl(sid, this.opts.origin) : shareUrl('A7F3', this.opts.origin);

    const headCells = r.cols.map((c) => `<span class="wr-th">${c.label}</span>`).join('');
    const row = (rr: typeof r.winRow, isWin: boolean) => `
      <div class="wr-row ${isWin ? 'is-win' : 'is-lose'} wr-row--${rr.team === 'A' ? 'a' : 'b'}">
        <span class="wr-team">${escapeHtml(rr.name)}</span>
        ${rr.cells.map((c) => `<span class="wr-cell ${c.won ? 'is-won' : ''}">${escapeHtml(c.v)}</span>`).join('')}
      </div>`;

    return `
      <div class="wr" data-screen="result">
        <header class="wr-head">
          <div class="wb-brand">
            ${LOGO_SVG}
            <div class="wb-brand__txt">
              <div class="wb-court">${escapeHtml(this.opts.courtName)}</div>
              <div class="wb-sub">Падел · парный матч</div>
            </div>
          </div>
          <div class="wb-status">
            <span class="wr-pill">${escapeHtml(r.status)}</span>
            <span class="wb-clock">${CLOCK_SVG}<span data-timer>00:00</span></span>
          </div>
        </header>

        <div class="wr-grid">
          <div class="wr-hero wr-hero--${r.winnerTeam === 'A' ? 'a' : 'b'}">
            <div class="wr-hero__top">
              <span class="wr-trophy">${TROPHY_SVG}</span>
              <div>
                <div class="wr-winner">${escapeHtml(r.winnerName)}</div>
                <span class="wr-badge">${escapeHtml(r.badge)}</span>
              </div>
            </div>
            <div class="wr-score">
              <span class="wr-score__big wr-c--${r.winnerTeam === 'A' ? 'a' : 'b'}">${escapeHtml(r.bigScore)}</span>
              <span class="wr-score__sep">:</span>
              <span class="wr-score__big wr-c--${r.loserTeam === 'A' ? 'a' : 'b'}">${escapeHtml(r.smallScore)}</span>
              <span class="wr-score__cap">по сетам</span>
            </div>
          </div>
          <div class="wr-vline"></div>
          <div class="wr-table">
            <div class="wr-row wr-row--head">
              <span class="wr-team wr-team--label">КОМАНДА</span>
              ${headCells}
            </div>
            ${row(r.winRow, true)}
            ${row(r.loseRow, false)}
            <div class="wr-dur">${escapeHtml(r.durationGames)}</div>
          </div>
        </div>

        <footer class="wr-foot">
          <span class="wr-foot__ver">AntScore · ${escapeHtml(this.opts.appVersion)} · только просмотр${mode === 'demo' ? ' · демо' : ''}</span>
          <div class="wb-follow">
            <div class="wb-follow__txt">
              <div class="wb-follow__cap">Поделиться результатом</div>
              <div class="wb-follow__url">${escapeHtml(url)}</div>
            </div>
            <div class="wb-qr">${qrSvg(sid || 'A7F3', 60)}</div>
          </div>
        </footer>
      </div>
    `;
  }
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
